import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { useTranslation } from 'react-i18next'
import {
	apiRejectOnboarding,
	apiUpdateOnboarding,
} from '../../services/api/onboarding'
import { apiGetPublicOrganizeGroups } from '../../services/api/social_group'
import { getApiErrorMessage } from '../../services/api/error'
import { redirectToSocial } from '../../services/social_handoff'
import { getRoleNameKey } from '../../utils/role_name_th'
import mascot from '../../assets/images/login-mascot.png'
import AuthHeaderActions from './auth_header_actions'
import CancelApplicationConfirmDialog from './cancel_application_confirm_dialog'
import {
	clearAuthSession,
	getToken,
	getUserInfo,
} from '../../services/auth_storage'
import {
	clearOnboardingSession,
	getOnboardingSession,
} from '../../services/onboarding_storage'
import './style/teacher_onboarding.css'

const toGroupOptions = (groups) => groups.map((group) => ({
	label: group.name,
	value: group.id,
}))

function TeacherOnboarding() {
	const { t } = useTranslation()
	const onboardingSession = useMemo(getOnboardingSession, [])
	const onboardingGroupId = onboardingSession?.data?.group_id || null
	const [teacherType, setTeacherType] = useState('affiliated')
	const [school, setSchool] = useState(null)
	const [teacherCode, setTeacherCode] = useState(`${onboardingSession?.data?.teacher_employee_id || ''}`)
	const [learningSite, setLearningSite] = useState(null)
	const [schoolOptions, setSchoolOptions] = useState([])
	const [learningSiteOptions, setLearningSiteOptions] = useState([])
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState('')
	const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false)
	const [error, setError] = useState('')

	const userInfo = useMemo(getUserInfo, [])

	const firstName = userInfo.first_name || userInfo.firstname || userInfo.name || t('onboarding.teacher.defaultName')
	const profileImage = userInfo.profile_picture || userInfo.profile_image || userInfo.avatar
	const roleName = t(getRoleNameKey(userInfo.role_id) || 'onboarding.teacher.role')
	const selectedGroupId = teacherType === 'affiliated' ? school : learningSite
	const hasValidTeacherCode = /^\d{5,10}$/.test(teacherCode)
	const canContinue = Boolean(selectedGroupId)
		&& (teacherType !== 'affiliated' || hasValidTeacherCode)
		&& !loading

	useEffect(() => {
		if (!getToken() || onboardingSession?.roleId !== 3) {
			window.location.replace('/login')
		}
	}, [onboardingSession])

	useEffect(() => {
		let active = true
		const loadGroups = async () => {
			setLoading(true)
			setError('')
			try {
				const commonParams = {
					sort_by: 'name',
					sort_direction: 'ASC',
					limit: 100,
					offset: 0,
				}
				const [institutionsResponse, learningStationsResponse] = await Promise.all([
					apiGetPublicOrganizeGroups(commonParams),
					apiGetPublicOrganizeGroups(commonParams),
				])
				if (!active) return

				const institutions = Array.isArray(institutionsResponse?.data?.groups)
					? institutionsResponse.data.groups
					: []
				const learningStations = Array.isArray(learningStationsResponse?.data?.groups)
					? learningStationsResponse.data.groups
					: []
				setSchoolOptions(toGroupOptions(institutions))
				setLearningSiteOptions(toGroupOptions(learningStations))

				if (onboardingGroupId && institutions.some((group) => group.id === onboardingGroupId)) {
					setTeacherType('affiliated')
					setSchool(onboardingGroupId)
					setLearningSite(null)
				} else if (onboardingGroupId && learningStations.some((group) => group.id === onboardingGroupId)) {
					setTeacherType('volunteer')
					setLearningSite(onboardingGroupId)
					setSchool(null)
				}
			} catch (loadError) {
				if (active) {
					setError(getApiErrorMessage(loadError, t('onboarding.teacher.errors.loadGroups')))
				}
			} finally {
				if (active) setLoading(false)
			}
		}

		loadGroups()
		return () => {
			active = false
		}
	}, [onboardingGroupId, t])

	const selectTeacherType = (type) => {
		setTeacherType(type)
		if (type === 'affiliated') setLearningSite(null)
		if (type === 'volunteer') setSchool(null)
	}

	const completeOnboarding = async (groupId, additionalData = {}) => {
		const response = await apiUpdateOnboarding(
			groupId ? { group_id: groupId, ...additionalData } : {},
			{ currentFormStep: 1 },
		)
		if (response?.status < 200 || response?.status >= 300) {
			throw new Error(response?.data?.message || t('onboarding.common.errors.save'))
		}
		clearOnboardingSession()
		await redirectToSocial(groupId, { targetPage: 'group' })
	}

	const continueToApplication = async () => {
		if (!canContinue || submitting) return
		setSubmitting('continue')
		setError('')
		try {
			await completeOnboarding(
				selectedGroupId,
				teacherType === 'affiliated' ? { teacher_employee_id: teacherCode } : {},
			)
		} catch (submitError) {
			setError(getApiErrorMessage(submitError, t('onboarding.common.errors.continue')))
			setSubmitting('')
		}
	}

	const skipOnboarding = async () => {
		if (submitting) return
		setSubmitting('skip')
		setError('')
		try {
			await completeOnboarding()
		} catch (submitError) {
			setError(getApiErrorMessage(submitError, t('onboarding.common.errors.skip')))
			setSubmitting('')
		}
	}

	const cancelApplication = async () => {
		if (submitting) return
		setSubmitting('cancel')
		setError('')
		try {
			const response = await apiRejectOnboarding()
			if (response?.status < 200 || response?.status >= 300) {
				throw new Error(response?.data?.message || t('onboarding.common.errors.cancelShort'))
			}
			clearAuthSession()
			clearOnboardingSession()
			window.location.assign('/login?logout=true&application_cancelled=true')
		} catch (cancelError) {
			setError(getApiErrorMessage(cancelError, t('onboarding.common.errors.cancel')))
			setSubmitting('')
			setCancelConfirmVisible(false)
		}
	}

	const hideCancelConfirm = () => {
		if (!submitting) setCancelConfirmVisible(false)
	}

	return (
		<main className='teacher-onboarding-page'>
			<CancelApplicationConfirmDialog
				visible={cancelConfirmVisible}
				loading={submitting === 'cancel'}
				onAccept={cancelApplication}
				onHide={hideCancelConfirm}
			/>
			<header className='teacher-onboarding-header'>
				<div className='teacher-onboarding-brand' aria-label={t('onboarding.common.brandAriaLabel')}>
					<span className='teacher-onboarding-logo'>{t('onboarding.common.logo')}</span>
					<span>{t('onboarding.common.education')} <strong>{t('onboarding.common.flexible')}</strong></span>
				</div>
				<div className='teacher-onboarding-header-actions'>
					<AuthHeaderActions />
					<div className='teacher-onboarding-user'>
						<span className='teacher-onboarding-avatar'>
							{profileImage ? <img src={profileImage} alt='' /> : '👩🏻‍🏫'}
						</span>
						<span className='teacher-onboarding-user-copy'>{t('onboarding.common.greeting', { name: firstName })}<small>{roleName}</small></span>
						<i className='pi pi-bars teacher-onboarding-mobile-user-icon' aria-hidden='true' />
					</div>
				</div>
			</header>

			<section className='teacher-onboarding-shell'>
				<div className='teacher-onboarding-intro'>
					<div className='teacher-onboarding-welcome'>
						<h1>{t('onboarding.common.welcome')}<br />{t('onboarding.common.education')}<strong>{t('onboarding.common.flexible')}</strong></h1>
						<p>{t('onboarding.common.welcomeDescription')}<br />{t('onboarding.teacher.welcomeInstruction')}</p>
					</div>
					<img src={mascot} alt={t('onboarding.common.mascotAlt')} />
				</div>

				<div className='teacher-onboarding-panel'>
					<div className='teacher-onboarding-panel-content'>
						<p className='teacher-onboarding-role'>{roleName}</p>
						<h1>{t('onboarding.teacher.title')}</h1>
						<p className='teacher-onboarding-subtitle'>{t('onboarding.teacher.subtitle')}</p>

						<div className='teacher-onboarding-options' role='radiogroup' aria-label={t('onboarding.teacher.typeAriaLabel')}>
							<section className={`teacher-onboarding-option${teacherType === 'affiliated' ? ' selected' : ''}`}>
								<button type='button' className='teacher-onboarding-option-heading' role='radio' aria-checked={teacherType === 'affiliated'} onClick={() => selectTeacherType('affiliated')}>
									<span className='teacher-onboarding-radio' />
									<span><strong>{t('onboarding.teacher.affiliated.title')}</strong><small>{t('onboarding.teacher.affiliated.description')}</small></span>
								</button>
								<div className='teacher-onboarding-affiliated-fields'>
									<label>
										<span>{t('onboarding.teacher.affiliated.schoolLabel')}</span>
										<Dropdown value={school} onChange={(event) => setSchool(event.value)} options={schoolOptions} filter placeholder={t(loading ? 'onboarding.common.loadingList' : 'onboarding.teacher.affiliated.schoolPlaceholder')} disabled={teacherType !== 'affiliated' || loading} aria-label={t('onboarding.teacher.affiliated.schoolLabel')} />
									</label>
									<label>
										<span>{t('onboarding.teacher.affiliated.teacherCodeLabel')}</span>
										<InputText
											value={teacherCode}
											onChange={(event) => setTeacherCode(event.target.value.replace(/\D/g, '').slice(0, 10))}
											placeholder={t('onboarding.teacher.affiliated.teacherCodePlaceholder')}
											disabled={teacherType !== 'affiliated'}
											inputMode='numeric'
											minLength={5}
											maxLength={10}
											aria-label={t('onboarding.teacher.affiliated.teacherCodeLabel')}
										/>
									</label>
								</div>
							</section>

							<section className={`teacher-onboarding-option${teacherType === 'volunteer' ? ' selected' : ''}`}>
								<button type='button' className='teacher-onboarding-option-heading' role='radio' aria-checked={teacherType === 'volunteer'} onClick={() => selectTeacherType('volunteer')}>
									<span className='teacher-onboarding-radio' />
									<span><strong>{t('onboarding.teacher.volunteer.title')}</strong><small>{t('onboarding.teacher.volunteer.description')}</small></span>
								</button>
								<label className='teacher-onboarding-volunteer-field'>
									<span>{t('onboarding.teacher.volunteer.siteLabel')}</span>
									<Dropdown value={learningSite} onChange={(event) => setLearningSite(event.value)} options={learningSiteOptions} filter placeholder={t(loading ? 'onboarding.common.loadingList' : 'onboarding.teacher.volunteer.sitePlaceholder')} disabled={teacherType !== 'volunteer' || loading} aria-label={t('onboarding.teacher.volunteer.siteLabel')} />
								</label>
							</section>
						</div>

						<div className='teacher-onboarding-help' role={error ? 'alert' : undefined}>
							<strong>{t(error ? 'onboarding.common.errorTitle' : 'onboarding.common.noteTitle')}</strong>
							<span>{error || t('onboarding.teacher.note')}</span>
						</div>
					</div>

					<div className='teacher-onboarding-actions'>
						<Button type='button' outlined icon={submitting === 'skip' ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-label={t('onboarding.teacher.actions.skipAriaLabel')} onClick={skipOnboarding} disabled={Boolean(submitting)} className='teacher-onboarding-skip'>
							<span className='teacher-onboarding-action-copy'><strong>{t('onboarding.teacher.actions.skip')}</strong><small>{t('onboarding.teacher.actions.skipDescription')}</small></span>
						</Button>
						<Button type='button' icon={submitting === 'continue' ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'} iconPos='right' aria-label={t('onboarding.common.actions.continueAriaLabel')} disabled={!canContinue || Boolean(submitting)} onClick={continueToApplication} className='teacher-onboarding-continue'>
							<span className='teacher-onboarding-action-copy'><strong>{t('onboarding.common.actions.continue')}</strong><small>{t('onboarding.teacher.actions.continueDescription')}</small></span>
						</Button>
						<Button type='button' outlined icon='pi pi-trash' aria-label={t('onboarding.teacher.actions.cancelAriaLabel')} onClick={() => setCancelConfirmVisible(true)} disabled={Boolean(submitting)} className='teacher-onboarding-cancel'>
							<span className='teacher-onboarding-action-copy'><strong>{t('onboarding.common.actions.cancel')}</strong><small>{t('onboarding.teacher.actions.cancelDescription')}</small></span>
						</Button>
					</div>
					<div className='teacher-onboarding-note'><i className='pi pi-info-circle' /><span>{t('onboarding.teacher.socialNote')}</span></div>
				</div>
			</section>
		</main>
	)
}

export default TeacherOnboarding
